import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { DashboardChartsAPI } from "../../../services/api/dashboard";
import { Box, Typography, useTheme, CircularProgress, Container } from "@mui/material";

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central">
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

export function DashboardCharts() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();
    const subdomain = localStorage.getItem("vendorsubdomain");

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await DashboardChartsAPI(subdomain);
            if (response.success) {
                // Transform the data to match Recharts expected format
                const transformedData = response.Percentages.map(item => ({
                    name: item.key,
                    value: parseFloat(item.values)
                }));
                setData(transformedData);
            }
        } catch (error) {
            console.error("Failed to fetch dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Color palette for the pie chart
    const COLORS = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main
    ];

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <CircularProgress />
            </Box>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography variant="body1">No chart data available</Typography>
            </Box>
        );
    }

    return (
        <Container>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Product Distribution
            </Typography>
            <Box sx={{ width: '100%', height: "calc(50vh - 20px)", position: "relative" }} >
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            cx="13%"
                            cy="55%"
                            labelLine={false}
                            label={renderCustomizedLabel}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="value"
                            animationDuration={500}
                            animationBegin={0}
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value) => [`${value}%`, 'Percentage']}
                            contentStyle={{
                                borderRadius: '8px',
                                border: 'none',
                                boxShadow: theme.shadows[3],
                                backgroundColor: theme.palette.background.paper
                            }}
                        />
                        <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="start"
                            wrapperStyle={{ position: "absolute", bottom: "-20px" }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </Box>
        </Container>
    );
}